import React, { useState } from 'react';
import { Box, Grid, Snackbar, Alert } from '@mui/material';
import {
  TodoHeader,
  UserTaskContainer,
  CreateTaskDialog,
  TaskDetailsDialog,
  DeleteConfirmDialog,
} from './Todo';
import { Task, SnackbarState } from '../types/todoTypes';
import { deleteTask } from '../services/todoService';

const TodoManagement: React.FC = () => {
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Selected task states
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Refresh trigger for statistics
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Handle opening task details
  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setDetailsDialogOpen(true);
  };

  // Handle delete request from card or dialog
  const handleDeleteRequest = (task: Task) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  // Handle confirmed deletion
  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    try {
      await deleteTask(taskToDelete.id);

      setSnackbar({
        open: true,
        message: 'Task deleted successfully',
        severity: 'success',
      });

      // Close dialogs
      setDeleteDialogOpen(false);
      setDetailsDialogOpen(false);
      setTaskToDelete(null);
      setSelectedTask(null);

      // Trigger statistics refresh
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error deleting task:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete task',
        severity: 'error',
      });
    }
  };

  // Handle task created
  const handleTaskCreated = () => {
    // Trigger statistics refresh
    setRefreshTrigger((prev) => prev + 1);
  };

  // Handle task updated
  const handleTaskUpdated = () => {
    // Trigger statistics refresh
    setRefreshTrigger((prev) => prev + 1);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      width: '100%',
      overflow: 'hidden'
    }}>
      {/* Header with statistics and create button */}
      <Box sx={{ px: 3, pt: 3, flexShrink: 0 }}>
        <TodoHeader
          onCreateClick={() => setCreateDialogOpen(true)}
          refreshTrigger={refreshTrigger}
        />
      </Box>

      {/* Dual container layout */}
      <Box sx={{ flex: 1, overflow: 'hidden', px: 3, pb: 2 }}>
        <Grid container spacing={3} sx={{ height: '100%' }}>
        {/* Container 1 */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <UserTaskContainer
            containerTitle="User Tasks - Container 1"
            containerNumber={1}
            onTaskClick={handleTaskClick}
            onTaskDelete={handleDeleteRequest}
          />
        </Grid>

        {/* Container 2 */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <UserTaskContainer
            containerTitle="User Tasks - Container 2"
            containerNumber={2}
            onTaskClick={handleTaskClick}
            onTaskDelete={handleDeleteRequest}
          />
        </Grid>
        </Grid>
      </Box>

      {/* Create Task Dialog */}
      <CreateTaskDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onTaskCreated={handleTaskCreated}
        setSnackbar={setSnackbar}
      />

      {/* Task Details Dialog */}
      <TaskDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onTaskUpdated={handleTaskUpdated}
        onDeleteRequested={handleDeleteRequest}
        setSnackbar={setSnackbar}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTaskToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        taskTitle={taskToDelete?.title || ''}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TodoManagement;
